/* eslint-disable */
import { useState, useRef } from "react";
import api from "../lib/api";

const MAX_IMAGES = 4;
const ACCEPT_TYPES = "image/jpeg,image/png,image/webp,image/heic,image/heif";

/**
 * Composer image picker. Manages up to 4 pending uploads:
 *  - Choose from photo library (native picker on iOS)
 *  - Preview grid with per-image remove
 *  - Reorder via move-left / move-right buttons (accessible, no drag-drop lib)
 *  - Uploads immediately on selection (parallel), so submit is fast
 *  - Retry on failed uploads
 *  - Reports uploaded image_ids to the parent via `onChange`
 */
export default function PostImagePicker({ imageIds, onChange, onPreviewsChange, disabled }) {
  const inputRef = useRef(null);
  // Each entry: { local_id, preview_url (object URL), status: uploading|done|error, image_id?, error? }
  const [items, setItems] = useState([]);

  const publish = (next) => {
    setItems(next);
    const ids = next.filter(i => i.status === "done" && i.image_id).map(i => i.image_id);
    onChange && onChange(ids);
    onPreviewsChange && onPreviewsChange(next);
  };

  const startUpload = async (item) => {
    try {
      const form = new FormData();
      form.append("files", item.file, item.file.name);
      const r = await api.post("/community/uploads", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const uploaded = r.data?.images?.[0];
      if (!uploaded?.image_id) throw new Error("No image returned");
      setItems((prev) => {
        const next = prev.map((x) => x.local_id === item.local_id
          ? { ...x, status: "done", image_id: uploaded.image_id, image_url: uploaded.image_url, width: uploaded.width, height: uploaded.height }
          : x);
        const ids = next.filter(i => i.status === "done" && i.image_id).map(i => i.image_id);
        onChange && onChange(ids);
        onPreviewsChange && onPreviewsChange(next);
        return next;
      });
    } catch (e) {
      const detail = e?.response?.data?.detail || "Upload failed";
      setItems((prev) => prev.map((x) => x.local_id === item.local_id
        ? { ...x, status: "error", error: detail } : x));
    }
  };

  const onFilesChosen = (files) => {
    if (!files || files.length === 0) return;
    const remaining = MAX_IMAGES - items.length;
    if (remaining <= 0) return;
    const toAdd = Array.from(files).slice(0, remaining).map((file) => ({
      local_id: `l_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      file,
      preview_url: URL.createObjectURL(file),
      status: "uploading",
    }));
    const next = [...items, ...toAdd];
    setItems(next);
    toAdd.forEach(startUpload);
  };

  const remove = (localId) => {
    const target = items.find(i => i.local_id === localId);
    if (target?.preview_url) URL.revokeObjectURL(target.preview_url);
    // Best-effort delete on the server (fire-and-forget)
    if (target?.image_id) {
      api.delete(`/community/images/${target.image_id}`).catch(() => {});
    }
    publish(items.filter(i => i.local_id !== localId));
  };

  const move = (localId, dir) => {
    const idx = items.findIndex(i => i.local_id === localId);
    const swapWith = idx + dir;
    if (idx < 0 || swapWith < 0 || swapWith >= items.length) return;
    const next = items.slice();
    [next[idx], next[swapWith]] = [next[swapWith], next[idx]];
    publish(next);
  };

  const retry = (item) => {
    setItems((prev) => prev.map((x) => x.local_id === item.local_id ? { ...x, status: "uploading", error: null } : x));
    startUpload(item);
  };

  const canAdd = items.length < MAX_IMAGES && !disabled;

  return (
    <div data-testid="composer-image-picker">
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={ACCEPT_TYPES}
        style={{ display: "none" }}
        onChange={(e) => { onFilesChosen(e.target.files); e.target.value = ""; }}
        data-testid="composer-file-input"
      />
      {items.length > 0 && (
        <div style={{
          display: "grid",
          gridTemplateColumns: items.length === 1 ? "1fr" :
                               items.length === 2 ? "1fr 1fr" : "repeat(2, 1fr)",
          gap: 8,
          margin: "10px 0",
        }}>
          {items.map((it, idx) => (
            <div key={it.local_id}
              style={{
                position: "relative",
                aspectRatio: items.length === 1 ? "16/10" : "1/1",
                borderRadius: 10,
                overflow: "hidden",
                background: "rgba(27,35,64,0.05)",
                border: "1px solid rgba(27,35,64,0.12)",
              }}
              data-testid={`composer-image-${idx}`}
            >
              <img src={it.preview_url}
                alt={`Upload ${idx + 1}`}
                style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              {it.status === "uploading" && (
                <div style={{
                  position: "absolute", inset: 0, background: "rgba(27,35,64,0.6)",
                  color: "#faf8f3", display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 12, letterSpacing: 0.16, textTransform: "uppercase",
                }}>Uploading…</div>
              )}
              {it.status === "error" && (
                <div style={{
                  position: "absolute", inset: 0, background: "rgba(176,64,64,0.85)",
                  color: "#faf8f3", display: "flex", flexDirection: "column",
                  alignItems: "center", justifyContent: "center", gap: 6, padding: 8, textAlign: "center",
                }}>
                  <div style={{ fontSize: 11 }}>{it.error}</div>
                  <button type="button" onClick={() => retry(it)}
                    style={{ padding: "4px 10px", border: "1px solid #faf8f3", background: "transparent", color: "#faf8f3", borderRadius: 999, fontSize: 11, cursor: "pointer" }}
                    data-testid={`composer-image-retry-${idx}`}>Retry</button>
                </div>
              )}
              <div style={{ position: "absolute", top: 6, right: 6, display: "flex", gap: 4 }}>
                {idx > 0 && (
                  <button type="button" onClick={() => move(it.local_id, -1)}
                    aria-label="Move image left"
                    data-testid={`composer-image-left-${idx}`}
                    style={_iconBtn}>←</button>
                )}
                {idx < items.length - 1 && (
                  <button type="button" onClick={() => move(it.local_id, 1)}
                    aria-label="Move image right"
                    data-testid={`composer-image-right-${idx}`}
                    style={_iconBtn}>→</button>
                )}
                <button type="button" onClick={() => remove(it.local_id)}
                  aria-label="Remove image"
                  data-testid={`composer-image-remove-${idx}`}
                  style={{ ..._iconBtn, background: "rgba(176,64,64,0.9)" }}>×</button>
              </div>
              <div style={{
                position: "absolute", bottom: 6, left: 6,
                background: "rgba(27,35,64,0.72)", color: "#faf8f3",
                padding: "2px 8px", borderRadius: 999, fontSize: 10, letterSpacing: 0.12,
              }}>{idx + 1}/{items.length}</div>
            </div>
          ))}
        </div>
      )}
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <button
          type="button"
          className="m-btn-ghost"
          onClick={() => inputRef.current?.click()}
          disabled={!canAdd}
          data-testid="composer-add-photos"
          style={{ padding: "6px 14px", fontSize: 12 }}
        >
          {items.length === 0 ? "+ Add photos" : `+ Add more (${items.length}/${MAX_IMAGES})`}
        </button>
        {items.length > 0 && (
          <span className="m-muted small">
            {items.filter(i => i.status === "uploading").length} uploading ·{" "}
            {items.filter(i => i.status === "done").length} ready
          </span>
        )}
      </div>
    </div>
  );
}

const _iconBtn = {
  width: 24, height: 24,
  border: "none",
  borderRadius: "50%",
  background: "rgba(27,35,64,0.72)",
  color: "#faf8f3",
  cursor: "pointer",
  fontSize: 13,
  lineHeight: 1,
  padding: 0,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
};
